import noop from 'lodash/fp/noop'
import { isNumber } from 'lodash/fp'
import {
  FocusEvent,
  HTMLInputTypeAttribute,
  useEffect,
  useRef,
  useState,
  ChangeEvent,
} from 'react'
import {
  Control,
  ControllerProps,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form'
import {
  FormControl,
  FormField as DefaultField,
  FormItem,
  FormMessage,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@wellesley/ui'

import removeSpaces from 'helpers/removeSpaces'
import { EmojiClickData } from '@wellesley/emoji-picker'
import { EmojiDropdown } from '../../EmojiDropdown'
import SwitchWithLabel from '../../SwitchWithLabel'
import { Eye, EyeOff } from 'lucide-react'
import { useSwitchState } from 'hooks/useSwitchState'
import RichTextArea from '../../RichTextArea'
import Label from '../Label'
import AutocompleteField from '../../AutocompleteField'
import FieldIcon from './FieldIcon'
import FieldCaption from './FieldCaption'

export type FormFieldProps = {
  control: Control
}

export type CommonFormFieldProps<
  T extends FieldValues,
  P extends FieldPath<T>,
> = Omit<
  ControllerProps<T, P> & {
    type?: HTMLInputTypeAttribute
    icon?: JSX.Element
    prefix?: string
    caption?: string
    maxChars?: number
    className?: string
    inputClassName?: string
    isSwitch?: boolean
    tooltipText?: string
    isTextArea?: boolean
    autoFocus?: boolean
    isRequired?: boolean
    placeholder?: string
    displayName?: string
    description?: string
    descriptionIcon?: JSX.Element
    isAutocomplete?: boolean
    readOnly?: boolean
    noSpaces?: boolean
    onAddEmoji?: (text: string, field?: ControllerRenderProps<T, P>) => void
    onFocus?: (
      event?: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void
    onBlur?: (
      event?: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
    onFieldChange?: (value: string | boolean) => void
    renderFieldAction?: () => JSX.Element | null
    formMessageClassName?: string
    descriptionClassName?: string
  },
  'render'
>

const charactersWarningLimit = 10

const CommonFormField = <T extends FieldValues, P extends FieldPath<T>>({
  type,
  icon,
  name,
  prefix,
  control,
  caption,
  maxChars,
  isSwitch,
  disabled,
  className,
  isRequired,
  placeholder,
  displayName,
  description,
  descriptionIcon,
  tooltipText,
  isTextArea = false,
  isAutocomplete = false,
  readOnly,
  noSpaces = false,
  autoFocus = false,
  onFocus,
  onBlur,
  onAddEmoji,
  onFieldChange,
  renderFieldAction,
  onKeyDown,
  inputClassName,
  formMessageClassName,
  descriptionClassName,
}: CommonFormFieldProps<T, P>) => {
  const [showPassword, , , triggerShowPassword] = useSwitchState()
  const EyeIcon = showPassword ? Eye : EyeOff

  const inputRef = useRef<HTMLInputElement>(null)
  const areaRef = useRef<HTMLTextAreaElement>(null)

  const [isFocused, setIsFocused] = useState(false)

  const prefixRef = useRef<HTMLSpanElement>(null)

  const isEmail = type === 'email'

  const mapValue = (value: string) => {
    return isEmail ? value.toLowerCase() : value
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (noSpaces && e.key === ' ') {
      e.preventDefault()
    }
    onKeyDown?.(e)
  }

  const handleInputChange = (
    field: ControllerRenderProps<T, P>,
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const value = mapValue(e.target.value)

    field.onChange({
      ...e,
      target: {
        ...e.target,
        value,
      },
    })

    onFieldChange?.(value)
  }

  const handleInputBlur = (
    field: ControllerRenderProps<T, P>,
    e: FocusEvent<HTMLInputElement>,
  ) => {
    field.onBlur()
    onBlur?.({
      ...e,
      target: {
        ...e.target,
        value: mapValue(e.target.value),
      },
    })
    setIsFocused(false)
  }

  const handlePasswordChange = (
    field: ControllerRenderProps<T, P>,
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    field.onChange(removeSpaces(e.target.value))
    onFieldChange?.(removeSpaces(e.target.value))
  }

  const handlePasswordBlur = (
    field: ControllerRenderProps<T, P>,
    e: FocusEvent<HTMLInputElement>,
  ) => {
    field.onBlur()
    setIsFocused(false)
    onBlur?.({
      ...e,
      target: {
        ...e.target,
        value: removeSpaces(e.target.value),
      },
    })
  }

  useEffect(() => {
    if (!prefix || !prefixRef.current || !inputRef.current) return

    const updatePrefixWidth = () => {
      const prefixWidth = prefixRef.current?.getBoundingClientRect().width
      if (prefixWidth && inputRef.current) {
        inputRef.current.style.paddingLeft = `${prefixWidth + 12}px`
      }
    }

    if (document.fonts && typeof document.fonts.ready === 'object') {
      void document.fonts.ready.then(() => {
        updatePrefixWidth()
      })
    }
  }, [prefix])

  return (
    <DefaultField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const handleAddEmoji = ({ emoji, isCustom }: EmojiClickData) => {
          const emojiText = isCustom ? `:${emoji}:` : emoji

          const input = inputRef.current || areaRef.current
          const position = input?.selectionEnd
          const currentValue = field.value as string

          const valueWithEmoji = isNumber(position)
            ? currentValue.slice(0, position) +
              emojiText +
              currentValue.slice(position)
            : currentValue

          if (maxChars && valueWithEmoji.length > maxChars) {
            return
          }

          onAddEmoji?.(valueWithEmoji, field)
        }

        const charactersCount = (field.value as string)?.length || 0
        const charactersLeft = (maxChars || 0) - charactersCount
        const showWarning = charactersLeft <= charactersWarningLimit

        const formItem = (
          <FormItem className={cn('mt-0 shrink-0', className)}>
            <div className="mb-xxs flex justify-between empty:hidden">
              {!isSwitch && displayName && (
                <Label {...{ isRequired, disabled, displayName }} />
              )}
              {maxChars && showWarning && isFocused && (
                <span className="text-sm text-gray-400">
                  {charactersLeft} characters left
                </span>
              )}
              {renderFieldAction?.()}
            </div>
            <div className="relative">
              {isSwitch ? (
                <FormControl>
                  <SwitchWithLabel
                    {...field}
                    {...(onFieldChange && {
                      onChange: (value) => {
                        field.onChange(value)
                        onFieldChange(value)
                      },
                    })}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    checked={!!field.value}
                    labelText={displayName || ''}
                  />
                </FormControl>
              ) : isTextArea ? (
                <FormControl>
                  <RichTextArea
                    {...field}
                    inputRef={areaRef}
                    text={field.value}
                    disabled={disabled}
                    placeholder={placeholder}
                    isForm={true}
                    maxLength={maxChars}
                    autoFocus={autoFocus}
                    onFocus={() => {
                      setIsFocused(true)
                      onFocus?.()
                    }}
                    setText={field.onChange || noop}
                    onChange={(e) => {
                      field.onChange(e)
                      onFieldChange?.(e.target.value)
                    }}
                    className={cn(
                      'resize-none p-3 text-base placeholder:text-base',
                      inputClassName,
                      onAddEmoji && 'pr-xxl',
                    )}
                  />
                </FormControl>
              ) : type === 'password' ? (
                <>
                  <FormControl
                    className={cn({
                      relative: !!prefix,
                    })}
                  >
                    <Input
                      {...field}
                      onFocus={(e) => {
                        setIsFocused(true)
                        onFocus?.(e)
                      }}
                      autoFocus={autoFocus}
                      type={showPassword ? 'text' : type}
                      placeholder={placeholder}
                      autoComplete="new-password"
                      className={cn('pr-8', inputClassName)}
                      onKeyDown={handleKeyDown}
                      maxLength={maxChars}
                      data-testid={field.name}
                      onChange={(e) => handlePasswordChange(field, e)}
                      onBlur={(e) => handlePasswordBlur(field, e)}
                    />
                  </FormControl>

                  {field.value && (
                    <EyeIcon
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                      className="absolute right-2 top-1/2 -translate-y-1/2 transform cursor-pointer text-gray-300"
                      onClick={triggerShowPassword}
                    />
                  )}
                </>
              ) : (
                <>
                  <FormControl
                    className={cn({
                      relative: !!prefix,
                    })}
                  >
                    {isAutocomplete ? (
                      <AutocompleteField
                        autoFocus={autoFocus}
                        text={field.value}
                        inputRef={inputRef}
                        setText={(text) => field.onChange(text)}
                        onChange={(e) => {
                          field.onChange(e)
                          onFieldChange?.(e.target.value)
                        }}
                        renderField={(props) => (
                          <Input
                            {...field}
                            onFocus={(e) => {
                              setIsFocused(true)
                              onFocus?.(e)
                            }}
                            autoFocus={autoFocus}
                            type={type || 'text'}
                            data-testid={field.name}
                            disabled={disabled}
                            readOnly={readOnly}
                            placeholder={placeholder}
                            onChange={props.handleChangeWithHashtagAutocomplete}
                            onKeyDown={handleKeyDown}
                            onBlur={(e) => {
                              field.onBlur()
                              onBlur?.(e)
                              setIsFocused(false)
                            }}
                            ref={inputRef}
                            className={cn(
                              !!icon && 'pl-11',
                              onAddEmoji && 'pr-10',
                              prefix && 'transition-none',
                              inputClassName,
                            )}
                          />
                        )}
                      />
                    ) : (
                      <>
                        <Input
                          {...field}
                          autoFocus={autoFocus}
                          type={type || 'text'}
                          onFocus={(e) => {
                            setIsFocused(true)
                            onFocus?.(e)
                          }}
                          data-testid={field.name}
                          disabled={disabled}
                          readOnly={readOnly}
                          placeholder={placeholder}
                          maxLength={maxChars}
                          onBlur={(e) => handleInputBlur(field, e)}
                          onChange={(e) => handleInputChange(field, e)}
                          onKeyDown={handleKeyDown}
                          ref={inputRef}
                          className={cn(
                            !!icon && 'pl-11',
                            onAddEmoji && 'pr-10',
                            prefix && 'transition-none',
                            inputClassName,
                          )}
                        />
                        <FieldCaption {...{ caption, disabled }} />
                      </>
                    )}
                  </FormControl>
                  {prefix && (
                    <span
                      ref={prefixRef}
                      className="absolute left-3 top-[12px] text-gray-400 "
                    >
                      {prefix}
                    </span>
                  )}

                  <FieldIcon
                    icon={icon}
                    disabled={disabled}
                    isFocused={isFocused}
                    isFilled={!!field.value}
                  />
                </>
              )}
              {onAddEmoji && (
                <div className="absolute right-4 top-3">
                  <EmojiDropdown
                    disabled={disabled}
                    onEmojiClick={handleAddEmoji}
                  />
                </div>
              )}
            </div>
            {description && !fieldState.error && (
              <p
                className={cn(
                  'mt-xxs gap-xxxs flex items-center text-sm text-gray-500',
                  descriptionClassName,
                )}
              >
                {descriptionIcon}
                {description}
              </p>
            )}
            <FormMessage className={formMessageClassName} />
          </FormItem>
        )

        return tooltipText ? (
          <Tooltip>
            <TooltipTrigger asChild>{formItem}</TooltipTrigger>
            <TooltipContent side="top" theme="dark" size="small" sideOffset={0}>
              {tooltipText}
            </TooltipContent>
          </Tooltip>
        ) : (
          formItem
        )
      }}
    />
  )
}

export default CommonFormField
